import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import {
  index,
  pgEnum,
  pgTable,
  text,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';

/**
 * Enum defining the standardized categories of waste materials.
 * - MIXED_SOLID_WASTE: General mixed waste that requires sorting
 * - MUNICIPAL_SOLID_WASTE: Household and commercial waste from municipalities
 * - CONSTRUCTION_AND_DEMOLITION_DEBRIS: Waste from construction/demolition sites
 * - INDUSTRIAL: Waste from industrial processes and manufacturing
 * - PATHOLOGICAL: Medical or biological waste requiring special handling
 * - RECYCLABLE: Materials that can be recycled or reprocessed
 * - ORGANIC: Biodegradable waste like food and yard waste
 * - OTHER: Miscellaneous materials not fitting other categories
 */
export const material_type = pgEnum('material_type', [
  'MIXED_SOLID_WASTE',
  'MUNICIPAL_SOLID_WASTE',
  'CONSTRUCTION_AND_DEMOLITION_DEBRIS',
  'INDUSTRIAL',
  'PATHOLOGICAL',
  'RECYCLABLE',
  'ORGANIC',
  'OTHER',
]);

/**
 * Represents a type of material that can be handled by waste management services.
 * Materials are categorized by type and can be associated with SKUs and pricing.
 * Used to track different waste streams and their specific handling requirements.
 */
export const material = pgTable(
  'material',
  {
    ...defaultFields.fieldsWithTenantId(),
    /** Display name of the material */
    name: varchar('name', { length: 255 }).notNull(),
    /** UUID of the user who last modified the record */
    lastModifiedBy: uuid('lastModifiedBy'),
    /** UUID of the user who created the record */
    createdBy: uuid('createdBy'),
    /** Standardized classification of the material */
    materialCanonicalType: material_type('materialCanonicalType'),
    /** ID from legacy system if this record was migrated */
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    /** Detailed description of the material and handling requirements */
    description: text('description'),
  },
  (table) => {
    return {
      createdAtTs_idx: index('material_createdAtTs_idx').on(table.createdAtTs),
      tenantId_idx: index('material_tenantId_idx').on(table.tenantId),
      tenantId_id_idx: index('material_tenant_id_idx').on(
        table.tenantId,
        table.id,
      ),
      updatedAtTs_idx: index('material_updatedAtTs_idx').on(table.updatedAtTs),
      updatedAtTs_idx1: index('material_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('material_versionNumber_idx').on(
        table.versionNumber,
      ),
      tenant_name_idx: index('idx_material_tenant_name').on(
        table.tenantId,
        table.name
      ),
    };
  },
);

/**
 * Historical record of changes to materials.
 * Tracks all modifications to material records over time for auditing and versioning.
 * Contains the same fields as material but allows nulls for partial updates.
 */
export const material_history = pgTable('material_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  name: varchar('name', { length: 255 }),
  lastModifiedBy: uuid('lastModifiedBy'),
  createdBy: uuid('createdBy'),
  materialCanonicalType: material_type('materialCanonicalType'),
  legacySystemId: varchar('legacySystemId', { length: 255 }),
  description: text('description'),
});

/** Type inference for material insert operations */
export type MaterialInsertSchema = typeof material.$inferInsert;
